import React, { useMemo, Component } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Languages, Globe } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import type { ModuleId, ModuleData } from '../../content';
import { getFamiliesForModule } from '../../content';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection } from '../../mascot';
import { useVariantUnlocks } from '../../mascot/useVariantUnlocks';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { getGrowthStage, getFamilyMasteryKey } from '../../mastery';
import type { AssessmentReport } from '../../assessment';
import { getRecentMilestones } from '../../utils/activityHelpers';
import { DjayAvatar } from '../../mascot/DjayAvatar';
// Journey Components
import { JourneyTrailhead } from './journey/JourneyTrailhead';
import { TrailStop } from './journey/TrailStop';
import { TrailConnector } from './journey/TrailConnector';
import { CheckpointFlag } from './journey/CheckpointFlag';
import { TreasureChest } from './journey/TreasureChest';
import { JourneyEmptyState } from './journey/JourneyEmptyState';
import { GhostTrophy } from './journey/GhostTrophy';
const SUBJECT_GROUPS: {
  label: string;
  icon: React.ElementType;
  accentColor: string;
  moduleIds: ModuleId[];
  djayMessage: string;
}[] = [{
  label: 'Rekenen',
  icon: Calculator,
  accentColor: 'bg-blue-100 text-blue-600',
  moduleIds: ['Getalbegrip', 'Basisrekenen', 'Metriek', 'Tijd', 'Geld'],
  djayMessage: 'Kijk hoe goed je kan rekenen! 🧮'
}, {
  label: 'Taal',
  icon: Languages,
  accentColor: 'bg-rose-100 text-rose-600',
  moduleIds: ['SpellingWW', 'SpellingRegel', 'Taalbegrip'],
  djayMessage: 'Jouw taal-skills groeien! 📝'
}, {
  label: 'Wereld & Studie',
  icon: Globe,
  accentColor: 'bg-emerald-100 text-emerald-600',
  moduleIds: ['Studievaardig', 'Topo'],
  djayMessage: 'Je ontdekt de wereld! 🌍'
}];
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onViewReport: (report: AssessmentReport) => void;
  onNavigate: (view: string) => void;
}
export function ProgressPage({
  state,
  selectedGroup,
  onViewReport,
  onNavigate
}: ProgressPageProps) {
  const {
    getModuleMasteryForGroup
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const currentStreak = state.dailyStreak?.currentWeekDays || 0;
  const records = useMemo(() => {
    let fastestTime = Infinity;
    let totalCorrect = 0;
    let totalAttempts = 0;
    if (state.questionStates) {
      Object.values(state.questionStates).forEach(qs => {
        qs.history?.forEach(attempt => {
          totalAttempts++;
          if (attempt.isCorrect) {
            totalCorrect++;
            if (attempt.responseTimeMs && attempt.responseTimeMs > 500) {
              fastestTime = Math.min(fastestTime, attempt.responseTimeMs);
            }
          }
        });
      });
    }
    return {
      fastestTime: fastestTime === Infinity ? null : fastestTime,
      accuracy: totalAttempts > 0 ? Math.round(totalCorrect / totalAttempts * 100) : 0,
      longestStreak: state.dailyStreak?.longestWeekStreak || 0,
      perfectSessions: state.dailyStreak?.totalPerfectSessions || 0,
      dailyGoals: state.dailyStreak?.totalDailyGoalsReached || 0
    };
  }, [state.questionStates, state.dailyStreak]);
  // Modules with mastery data
  const activeModules = useMemo(() => {
    return MODULES.filter(module => {
      const families = getFamiliesForModule(module.id);
      return families.length > 0 && families.some(f => {
        const key = getFamilyMasteryKey(f.familyId, selectedGroup);
        return state.familyMastery?.[key] !== undefined;
      });
    });
  }, [state.familyMastery, selectedGroup]);
  const overallMastery = useMemo(() => {
    if (activeModules.length === 0) return 0;
    let masterySum = 0;
    activeModules.forEach(m => {
      masterySum += getModuleMasteryForGroup(m.id);
    });
    return Math.round(masterySum / activeModules.length);
  }, [activeModules, getModuleMasteryForGroup]);
  const overallGrowthStage = getGrowthStage(overallMastery);
  const milestones = getRecentMilestones(state, selectedGroup);
  // Collectible bridge: surface the NEXT Djay variant the learner is closest
  // to unlocking. Secret/always-on variants are excluded so the teaser always
  // points at a concrete, achievable goal. (`hasDiscoveredLongPress` only
  // gates the secret 'goud' variant, which we exclude here, so `false` is safe.)
  const {
    variants
  } = useVariantUnlocks(false);
  const nextUnlock = useMemo(() => {
    const candidates = variants.filter(v => !v.unlocked && v.rarity !== 'secret' && v.id !== 'default' && v.progress.max > 1);
    if (candidates.length === 0) return null;
    return candidates.sort((a, b) => b.progress.current / b.progress.max - a.progress.current / a.progress.max)[0];
  }, [variants]);
  // Pre-calculate the trail layout
  const trailNodes: {
    type: 'territory' | 'stop' | 'milestone';
    data: any;
    align?: 'left' | 'right' | 'center';
  }[] = [];
  let alignToggle = false; // false = left, true = right
  if (activeModules.length > 0) {
    SUBJECT_GROUPS.forEach(group => {
      const groupModules = group.moduleIds.map(id => activeModules.find(m => m.id === id)).filter((m): m is ModuleData => !!m);
      if (groupModules.length > 0) {
        // Add Territory Marker
        trailNodes.push({
          type: 'territory',
          data: group,
          align: 'center'
        });
        // Add Modules
        groupModules.forEach(module => {
          trailNodes.push({
            type: 'stop',
            data: module,
            align: alignToggle ? 'right' : 'left'
          });
          alignToggle = !alignToggle;
        });
        // Sprinkle a milestone if available and we haven't used them all
        if (milestones.length > 0) {
          const ms = milestones.shift();
          if (ms) {
            trailNodes.push({
              type: 'milestone',
              data: ms,
              align: alignToggle ? 'right' : 'left'
            });
            alignToggle = !alignToggle;
          }
        }
      }
    });
  }
  return <PageContainer className="pb-safe px-0 sm:px-6 bg-[#FAFAF8] min-h-screen" data-id="element-1863">
      {activeModules.length === 0 ? <motion.div variants={itemVariants} data-id="element-1864">
          <JourneyEmptyState onNavigate={onNavigate} data-id="element-1865" />
        </motion.div> : <div className="flex flex-col w-full max-w-2xl mx-auto relative overflow-hidden sm:overflow-visible" data-id="element-1866">
          {/* Background Winding Line (CSS fallback for the trail) */}
          <div className="absolute top-32 bottom-32 left-1/2 -translate-x-1/2 w-1.5 bg-slate-200/50 rounded-full z-0" data-id="element-1867" />

          {/* 1. Trailhead */}
          <motion.div variants={itemVariants} className="relative z-10" data-id="element-1868">
            <div className="flex justify-center mb-[-20px] relative z-20" data-id="element-1869">
              <div className="w-32 h-32" data-id="element-1870">
                <DjayAvatar emotion="celebrating" variant={selectedVariant} data-id="element-1871" />
              </div>
            </div>
            <JourneyTrailhead overallGrowthStage={overallGrowthStage} overallMastery={overallMastery} currentStreak={currentStreak} djayVariant={selectedVariant} data-id="element-1872" />
          </motion.div>

          {/* 2. The Trail */}
          <div className="flex flex-col w-full px-4 sm:px-8 py-8 relative z-10" data-id="element-1873">
            {trailNodes.map((node, index) => {
          const prevNode = index > 0 ? trailNodes[index - 1] : null;
          const isFirstStop = index === 0;
          return <motion.div key={`node-${index}`} variants={itemVariants} className="flex flex-col w-full" data-id="element-1874">
                  {/* Connector from previous node */}
                  {!isFirstStop && node.type === 'stop' && prevNode?.type === 'stop' && <TrailConnector direction={prevNode.align === 'left' ? 'left-to-right' : 'right-to-left'} colorClass={node.data.color} data-id="element-1875" />}
                  {/* Straight connector if coming from territory marker */}
                  {!isFirstStop && node.type === 'stop' && prevNode?.type === 'territory' && <TrailConnector direction="straight" colorClass={node.data.color} data-id="element-1876" />}

                  {/* Node Content */}
                  {node.type === 'territory' && <div className="self-center my-8 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border-2 border-slate-200 shadow-sm flex items-center gap-2 relative z-10" data-id="element-1877">
                      <node.data.icon className={`w-5 h-5 ${node.data.accentColor.split(' ')[1]}`} data-id="element-1878" />
                      <span className="font-black text-slate-700 text-sm uppercase tracking-wider" data-id="element-1879">
                        {node.data.label}
                      </span>
                    </div>}

                  {node.type === 'stop' && <TrailStop module={node.data} families={getFamiliesForModule(node.data.id)} state={state} selectedGroup={selectedGroup} align={node.align as 'left' | 'right'} data-id="element-1880" />}

                  {node.type === 'milestone' && <CheckpointFlag label={node.data.label} emoji={node.data.emoji} align={node.align as 'left' | 'right'} data-id="element-1881" />}
                </motion.div>;
        })}
          </div>

          {/* 2b. Collectible bridge — next unlock teaser */}
          {nextUnlock && <motion.div variants={itemVariants} className="flex flex-col w-full px-4 sm:px-8 pb-8 -mt-4" data-id="element-1882">
              <GhostTrophy variant={nextUnlock} align="center" onOpenCollection={() => onNavigate('collection')} data-id="element-1883" />
            </motion.div>}

          {/* 3. Trail's End (Treasure Chest) */}
          <motion.div variants={itemVariants} data-id="element-1884">
            <TreasureChest fastestTime={records.fastestTime} accuracy={records.accuracy} longestStreak={records.longestStreak} perfectSessions={records.perfectSessions} dailyGoals={records.dailyGoals} onOpenCollection={() => onNavigate('collection')} data-id="element-1885" />
          </motion.div>
        </div>}
    </PageContainer>;
}